import { useEffect, useState } from "react"

import { supabase } from "@/lib/supabase"

export interface SubTeamOption {
  id: string
  value: string
  label: string
  type: "DRIVER_MANAGER_TEAM" | "DSP" | "GENERAL"
}

// Base sub-team values that are always included
export const baseSubTeamValues = ["HIVED", "Self Employed"] as const

/**
 * Hook to fetch DSPs and Driver Manager Teams from Supabase
 * Returns options formatted for use in select/multi-select components
 */
export function useSubTeams() {
  const [subTeamOptions, setSubTeamOptions] = useState<{ value: string; label: string }[]>([])
  const [subTeamOptionsWithIds, setSubTeamOptionsWithIds] = useState<SubTeamOption[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadOptions = async () => {
      setLoading(true)
      setError(null)

      try {
        // Fetch driver manager teams (only active ones)
        const { data: teams, error: teamsError } = await supabase
          .from("driver_manager_teams")
          .select("id, team, airtable_record_id")
          .eq("active", true)
          .order("team")

        if (teamsError) throw teamsError

        // Fetch DSPs (only active ones)
        const { data: dsps, error: dspsError } = await supabase
          .from("dsps")
          .select("id, dsp, airtable_record_id")
          .eq("active", true)
          .order("dsp")

        if (dspsError) throw dspsError

        // Map to SubTeamOption format
        const teamOpts: SubTeamOption[] =
          teams?.map((t) => ({
            id: t.airtable_record_id,
            value: t.team,
            label: t.team,
            type: "DRIVER_MANAGER_TEAM" as const,
          })) ?? []

        const dspOpts: SubTeamOption[] =
          dsps?.map((d) => ({
            id: d.airtable_record_id,
            value: d.dsp,
            label: d.dsp,
            type: "DSP" as const,
          })) ?? []

        // Add base values
        const baseOpts: SubTeamOption[] = [
          {
            id: "subteam-hived",
            value: baseSubTeamValues[0],
            label: baseSubTeamValues[0],
            type: "GENERAL",
          },
          {
            id: "subteam-self-employed",
            value: baseSubTeamValues[1],
            label: baseSubTeamValues[1],
            type: "GENERAL",
          },
        ]

        const allOptions = [...baseOpts, ...teamOpts, ...dspOpts]
        setSubTeamOptionsWithIds(allOptions)
        setSubTeamOptions(allOptions.map((t) => ({ value: t.value, label: t.label })))
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : "Failed to load sub-teams"
        setError(errorMessage)
        console.error("Error loading sub-teams:", e)
      } finally {
        setLoading(false)
      }
    }

    loadOptions()
  }, [])

  return {
    subTeamOptions,
    subTeamOptionsWithIds,
    loading,
    error,
  }
}
